import { useCallback, useEffect } from 'react'
import { LOCAL_STORAGE } from '../constants/localStorage'
import { SESSION_STORAGE } from '../constants/sessionStorage'
import { LocalStorageService } from '../Services/LocalStorage'
import { useAnimalStore } from '../Stores/animalStore'
import { useReportStore } from '../Stores/reportStore'
import { TODAY, useSimulatorStore } from '../Stores/simulatorStore'
import { useAnimalValidateStore } from '../Stores/useAnimalValidateStore'
import { useFillDataForSimulationStore } from '../Stores/useFillDataForSimulationStore'
import { useLoadPolicyHolderReferenceDataStore } from '../Stores/useLoadPolicyHolderReferenceDataStore'
import { useRunPetisSimulationStore } from '../Stores/useRunPetisSimulationStore'
import { AnimalsSecured } from '../Types/AnimalStore'
import { EntityPayload } from '../Types/EntityDetails'

export const useNewSimulation = () => {
  const { setFillDataForSimulationRequest, fillDataForSimulationRequest } =
    useFillDataForSimulationStore()
  const { setPolicyHolderReferenceData, policyHolderReferenceData } =
    useLoadPolicyHolderReferenceDataStore()
  const { setNewAnimal, animalsList } = useAnimalStore()
  const { setIsValid } = useAnimalValidateStore()
  const { setEnabledRunPetisRequest } = useRunPetisSimulationStore()
  const { isProspect, report, setReport, setIsProspect } = useReportStore()
  const {
    setEntity,
    setEntitySelected,
    entitySelected,
    entity,
    breeds,
    setBreeds,
    initialDate,
    setInitialDate,
    setOwnerFiscalNumber,
  } = useSimulatorStore()

  const handleNewSimulation = useCallback(() => {
    setEnabledRunPetisRequest(true)
    const storageAnimalList: AnimalsSecured[] =
      LocalStorageService.get(LOCAL_STORAGE.animalStore) !== 'undefined' &&
      LocalStorageService.get(LOCAL_STORAGE.animalStore) !== undefined
        ? JSON.parse(LocalStorageService.get(LOCAL_STORAGE.animalStore) || '[]')
        : []

    storageAnimalList.forEach((animal) => {
      setNewAnimal({
        id: animal.id,
        OwnerId: animal.OwnerId,
        name: animal.name,
        birthDate: animal.birthDate,
        species: animal.species,
        breed: animal.breed,
        questionary: animal.questionary,
        speciesCode: animal.speciesCode,
        breedCode: animal.breedCode,
      })
      setIsValid({
        [`questionsAnimal${animal.id}`]: true,
        [`formAnimal${animal.id}`]: true,
      })
    })

    setInitialDate(
      LocalStorageService.get(LOCAL_STORAGE.initialDate) !== 'undefined' &&
        LocalStorageService.get(LOCAL_STORAGE.initialDate) !== undefined
        ? JSON.parse(LocalStorageService.get(LOCAL_STORAGE.initialDate)!)
        : TODAY,
    )

    setFillDataForSimulationRequest(
      LocalStorageService.get(LOCAL_STORAGE.fillDataForSimulation) !==
        'undefined' &&
        LocalStorageService.get(LOCAL_STORAGE.fillDataForSimulation) !==
          undefined
        ? JSON.parse(
            LocalStorageService.get(LOCAL_STORAGE.fillDataForSimulation) ||
              '{}',
          )
        : null,
    )

    setPolicyHolderReferenceData(
      LocalStorageService.get(LOCAL_STORAGE.policyHolderReferenceData) !==
        'undefined' &&
        LocalStorageService.get(LOCAL_STORAGE.policyHolderReferenceData) !==
          undefined
        ? JSON.parse(
            LocalStorageService.get(LOCAL_STORAGE.policyHolderReferenceData) ||
              '{}',
          )
        : null,
    )

    const entityStore: EntityPayload =
      LocalStorageService.get(LOCAL_STORAGE.entity) !== 'undefined' &&
      LocalStorageService.get(LOCAL_STORAGE.entity) !== undefined
        ? JSON.parse(LocalStorageService.get(LOCAL_STORAGE.entity) || '{}')
        : null

    const entitySelectedStore =
      LocalStorageService.get(LOCAL_STORAGE.entitySelected) !== 'undefined' &&
      LocalStorageService.get(LOCAL_STORAGE.entitySelected) !== undefined
        ? JSON.parse(
            LocalStorageService.get(LOCAL_STORAGE.entitySelected) || '{}',
          )
        : null

    setEntity(entityStore)
    setEntitySelected(entitySelectedStore)
    setBreeds(
      LocalStorageService.get(LOCAL_STORAGE.breeds) !== 'undefined' &&
        LocalStorageService.get(LOCAL_STORAGE.breeds) !== undefined
        ? JSON.parse(LocalStorageService.get(LOCAL_STORAGE.breeds) || '{}')
        : null,
    )

    const prospectEntityStore =
      LocalStorageService.get(LOCAL_STORAGE.prospectEntity) !== 'undefined' &&
      LocalStorageService.get(LOCAL_STORAGE.prospectEntity) !== undefined
        ? JSON.parse(
            LocalStorageService.get(LOCAL_STORAGE.prospectEntity) || '{}',
          )
        : null

    if (prospectEntityStore.isProspect) {
      setIsProspect(true)
      setOwnerFiscalNumber(prospectEntityStore?.report?.vatNumber || '')
      setReport({
        vatNumber: prospectEntityStore?.report?.vatNumber || '',
        name: prospectEntityStore?.report?.name || '',
        phone: prospectEntityStore?.report.phone || '',
        email: prospectEntityStore?.report?.email || '',
      })
    }
  }, [])

  const setSimulationLocalStorage = () => {
    sessionStorage.removeItem(SESSION_STORAGE.oldRpsQuoteId)
    sessionStorage.removeItem(SESSION_STORAGE.rpsQuoteId)

    LocalStorageService.set(
      LOCAL_STORAGE.initialDate,
      JSON.stringify(initialDate),
    )
    LocalStorageService.set(
      LOCAL_STORAGE.fillDataForSimulation,
      JSON.stringify(fillDataForSimulationRequest),
    )

    LocalStorageService.set(
      LOCAL_STORAGE.policyHolderReferenceData,
      JSON.stringify(policyHolderReferenceData),
    )

    LocalStorageService.set(
      LOCAL_STORAGE.animalStore,
      JSON.stringify(animalsList),
    )

    LocalStorageService.set(LOCAL_STORAGE.entity, JSON.stringify(entity))
    LocalStorageService.set(
      LOCAL_STORAGE.entitySelected,
      JSON.stringify(entitySelected),
    )

    LocalStorageService.set(LOCAL_STORAGE.breeds, JSON.stringify(breeds))

    if (isProspect) {
      LocalStorageService.set(
        LOCAL_STORAGE.prospectEntity,
        JSON.stringify({
          isProspect,
          report,
        }),
      )
    }
  }

  const handleClearLocalStorage = () => {
    Object.values(LOCAL_STORAGE).forEach((key) => {
      LocalStorageService.remove(key)
    })
  }

  useEffect(() => {
    Object.values(LOCAL_STORAGE).forEach((key) => {
      if (LocalStorageService.get(key) === 'undefined') {
        LocalStorageService.remove(key)
      }
    })
  }, [])

  return {
    handleNewSimulation,
    handleClearLocalStorage,
    setSimulationLocalStorage,
  }
}
